import React, { useRef, useState } from 'react';
import { Button, Dropdown, Space } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  BuildOutlined,
  CheckOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { Video, VideoStatus } from '@monorepo/types';
import { LoadingState, useLoading } from '@monorepo/react-components';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../../../../helpers/use-store';
import DeleteVideoModal, {
  DeleteVideoModalActions,
} from '../../../videos/delete-video-modal';
import { useNotification } from '../../../../hooks/use-notification';
import type { MenuProps } from 'antd';

interface VideoActionsProps {
  video: Video;
  refetch: () => void;
}

const VideoActions: React.FC<VideoActionsProps> = ({ video, refetch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const notificationApi = useNotification();
  const {
    dataStore: { seriesStore, videoStore },
  } = useStore();
  const deleteModalRef = useRef<DeleteVideoModalActions>(null);
  const {
    loadingState: publishLoading,
    updateLoadingState: updatePublishLoadingState,
  } = useLoading();
  const {
    loadingState: createAssetsLoading,
    updateLoadingState: updateCreateAssetsLoadingState,
  } = useLoading();
  const [currentVideoCreatingAssetsId, updateCurrentVideoCreatingAssetsId] =
    useState<string | null>(null);

  const handleCreateAssets = async () => {
    if (!video.seriesId) {
      return;
    }

    updateCreateAssetsLoadingState(LoadingState.Loading);
    updateCurrentVideoCreatingAssetsId(video._id);

    try {
      await seriesStore.generateSeriesVideoAssets(video.seriesId, video._id);
      await refetch();

      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description: t('series.videos.actions.createAssetsSuccess'),
      });
    } catch (e) {
      notificationApi.error({
        placement: 'top',
        icon: '',
        message: t('common.error'),
        description: t('series.videos.actions.createAssetsError'),
      });

      console.error(`failed creating assets for video: ${video._id}`, e);
    } finally {
      updateCreateAssetsLoadingState(LoadingState.Loaded);
      updateCurrentVideoCreatingAssetsId(null);
    }
  };

  const handlePublish = async () => {
    try {
      updatePublishLoadingState(LoadingState.Loading);
      await videoStore.updateGeneralInfo(video._id, {
        status: VideoStatus.Published,
      });
      refetch();

      notificationApi.success({
        placement: 'top',
        message: t('common.success'),
        description: t('series.videos.actions.publishSuccess'),
      });
    } catch (error) {
      console.error('Failed to publish video:', error);
      notificationApi.error({
        placement: 'top',
        message: t('common.error'),
        description: t('series.videos.actions.publishError'),
      });
    } finally {
      updatePublishLoadingState(LoadingState.Loaded);
    }
  };

  const handleDelete = () => {
    deleteModalRef.current?.open(video._id);
  };

  const isInitial = video.status === VideoStatus.Initial;
  const isPublished = video.status === VideoStatus.Published;
  const isLoading = createAssetsLoading === LoadingState.Loading;
  const canPublish = video.videoUrl && !isPublished;
  const isActionLoading =
    createAssetsLoading === LoadingState.Loading ||
    publishLoading === LoadingState.Loading;

  const items: MenuProps['items'] = [];

  if (isInitial) {
    items.push({
      key: 'create-assets',
      icon: <BuildOutlined />,
      label: t('series.videos.actions.createAssets'),
      disabled: isLoading && video._id !== currentVideoCreatingAssetsId,
      onClick: handleCreateAssets,
    });
  }

  if (!isInitial) {
    items.push({
      key: 'publish',
      icon: <CheckOutlined />,
      label: t('series.videos.actions.markPublished'),
      disabled: !canPublish,
      onClick: handlePublish,
    });
  }

  items.push({
    key: 'delete',
    icon: <DeleteOutlined />,
    label: t('common.delete'),
    danger: true,
    onClick: handleDelete,
  });

  return (
    <Space>
      <Button
        disabled={isInitial}
        icon={<EyeOutlined />}
        onClick={() => navigate(`/videos/${video._id}`)}
      >
        {t('series.videos.actions.viewButton')}
      </Button>

      <Dropdown menu={{ items }} trigger={['click']}>
        <Button loading={isActionLoading}>
          <Space>
            {t('series.videos.actions.actionsButton')}
            <MoreOutlined />
          </Space>
        </Button>
      </Dropdown>
      <DeleteVideoModal ref={deleteModalRef} onSuccess={refetch} />
    </Space>
  );
};

export default VideoActions;
